import React, { useEffect, useState } from "react";
import Joi from "joi";
import toast from "react-hot-toast";
import { getUser, setUser } from "../utils/helper";
import { updateAdmin } from "../services/adminService";
import { getSettings, updateSettings } from "../services/settingsService";
import { useTheme } from "../contexts/ThemeContext";
import { useSettings } from "../contexts/SettingsContext";
import Skeleton from "../components/ui/Skeleton";
import AiConnectors from "../components/settings/AiConnectors";
import "../assets/css/pages/settings.css";


function SlackLogo() {
  return (
    <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_2415_8851)">
      <path d="M14.7407 21.085C12.4207 21.085 10.5391 22.9666 10.5391 25.2866V35.7966C10.5391 38.1166 12.4207 39.9983 14.7407 39.9983C17.0607 39.9983 18.9424 38.1166 18.9424 35.7966V25.2866C18.9407 22.9666 17.0591 21.085 14.7407 21.085Z" fill="#E91E63"/>
      <path d="M0.03125 25.287C0.03125 27.6087 1.91458 29.492 4.23625 29.492C6.55792 29.492 8.44125 27.6087 8.44125 25.287V21.082H4.23958C4.23792 21.082 4.23792 21.082 4.23625 21.082C1.91458 21.082 0.03125 22.9654 0.03125 25.287Z" fill="#E91E63"/>
      <path d="M14.7452 -0.00195312C14.7435 -0.00195312 14.7418 -0.00195312 14.7402 -0.00195312C12.4185 -0.00195312 10.5352 1.88138 10.5352 4.20305C10.5352 6.52471 12.4185 8.40805 14.7402 8.40805H18.9418V4.20305C18.9418 4.20138 18.9418 4.19805 18.9418 4.19471C18.9402 1.87638 17.0618 -0.00195312 14.7452 -0.00195312Z" fill="#00BCD4"/>
      <path d="M4.20891 18.95H14.7389C17.0606 18.95 18.9439 17.0667 18.9439 14.745C18.9439 12.4234 17.0606 10.54 14.7389 10.54H4.20891C1.88724 10.54 0.00390625 12.4234 0.00390625 14.745C0.00390625 17.0667 1.88724 18.95 4.20891 18.95Z" fill="#00BCD4"/>
      <path d="M35.7592 10.5381C33.4408 10.5381 31.5625 12.4164 31.5625 14.7348V14.7431V18.9481H35.7642C38.0858 18.9481 39.9692 17.0648 39.9692 14.7431C39.9692 12.4214 38.0858 10.5381 35.7642 10.5381C35.7625 10.5381 35.7608 10.5381 35.7592 10.5381Z" fill="#4CAF50"/>
      <path d="M21.0664 4.20362V14.7453C21.0664 17.0653 22.9481 18.947 25.2681 18.947C27.5881 18.947 29.4697 17.0653 29.4697 14.7453V4.20362C29.4697 1.88362 27.5881 0.00195312 25.2681 0.00195312C22.9481 0.00195312 21.0664 1.88362 21.0664 4.20362Z" fill="#4CAF50"/>
      <path d="M29.4697 35.7935C29.4697 33.4735 27.5881 31.5918 25.2681 31.5918H21.0664V35.7968C21.0681 38.1151 22.9481 39.9951 25.2681 39.9951C27.5881 39.9951 29.4697 38.1135 29.4697 35.7935Z" fill="#FF9800"/>
      <path d="M35.7975 21.082H25.2675C22.9458 21.082 21.0625 22.9654 21.0625 25.287C21.0625 27.6087 22.9458 29.492 25.2675 29.492H35.7975C38.1192 29.492 40.0025 27.6087 40.0025 25.287C40.0025 22.9654 38.1192 21.082 35.7975 21.082Z" fill="#FF9800"/>
      </g>
      <defs>
      <clipPath id="clip0_2415_8851">
      <rect width="40" height="40" fill="white"/>
      </clipPath>
      </defs>
    </svg>

  );
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TIMEZONES = ["Asia/Dhaka", "UTC", "Asia/Kolkata", "Asia/Dubai", "America/New_York", "Europe/London"];
const ACCENTS = ["#09A7DF", "#665DA9", "#F0679F", "#FDB849"];
const toTimeInput = (t) => (t ? String(t).slice(0, 5) : "");

// Admin-configurable attendance-detection keywords. Defaults mirror the bot's DEFAULT_KEYWORDS
// (bot/helper.ts); the `hint` explains each category's matching behaviour to the admin.
const KEYWORD_DEFAULTS = {
  keywords_signin: ["signing in", "sign in", "signed in"],
  keywords_signout: ["signing out", "sign out", "signed out"],
  keywords_remote: ["remote"],
  keywords_break: ["break", "brb", "lunch"],
  keywords_back: ["back", "resume", "continue", "continuing"],
};
const KEYWORD_CATS = [
  { key: "keywords_signin", label: "Sign In", hint: "Matched anywhere in the message" },
  { key: "keywords_signout", label: "Sign Out", hint: "Matched anywhere in the message" },
  { key: "keywords_remote", label: "Remote", hint: "Marks a sign-in as remote work" },
  { key: "keywords_break", label: "Break", hint: "Whole-word match (won't fire on 'breakfast')" },
  { key: "keywords_back", label: "Back", hint: "Whole-word match (return from break)" },
];
// Trim, lowercase (the bot matches case-insensitively), drop blanks and duplicates.
const cleanKeywordList = (arr) => [...new Set((arr || []).map((s) => String(s).trim().toLowerCase()).filter(Boolean))];

const accountSchema = Joi.object({
  email: Joi.string().email({ tlds: { allow: false } }).messages({
    "string.empty": "Please enter your email address!",
    "string.email": "Please enter a valid email address!",
  }),
  password: Joi.string().allow("").min(2).messages({ "string.min": "Password can't be 1 character long!" }),
  reTypedPassword: Joi.string().valid(Joi.ref("password")).messages({ "any.only": "Your re-typed password did not match!" }),
});

const Settings = () => {
  const { accent, setAccent } = useTheme();
  const { refresh: refreshSettings } = useSettings();
  const { id, email: prevEmail, name, title } = getUser() || {};

  // ── workspace / hours / integrations / accent ──
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [loaded, setLoaded] = useState(null); // pristine copy for Cancel

  // ── account ──
  const [email, setEmail] = useState(prevEmail || "");
  const [password, setPassword] = useState("");
  const [reTypedPassword, setReTypedPassword] = useState("");
  const [acctSubmitting, setAcctSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      const s = await getSettings();
      const f = s || { organisation_name: "Startise", timezone: "Asia/Dhaka", day_start: "09:00:00", day_end: "18:00:00", grace_period_min: 15, working_days: ["Sun", "Mon", "Tue", "Wed", "Thu"], slack_connected: 1, gcal_connected: 0, brand_accent: accent };
      const shape = {
        organisation_name: f.organisation_name || "",
        timezone: f.timezone || "Asia/Dhaka",
        day_start: toTimeInput(f.day_start),
        day_end: toTimeInput(f.day_end),
        grace_period_min: f.grace_period_min ?? 15,
        working_days: Array.isArray(f.working_days) ? f.working_days : [],
        max_shift_hours: f.max_shift_hours ?? 18,
        sessions_auto_close_enabled: f.sessions_auto_close_enabled ?? true,
        slack_connected: !!f.slack_connected,
        gcal_connected: !!f.gcal_connected,
        brand_accent: f.brand_accent || accent,
        reward_signin_deadline: toTimeInput(f.reward_signin_deadline) || "09:30",
        reward_min_hours: f.reward_min_hours ?? 8,
        reward_max_absent_days: f.reward_max_absent_days ?? 2,
        reminder_signin_after_minutes: f.reminder_signin_after_minutes ?? 60,
        reminder_long_break_min: f.reminder_long_break_min ?? 60,
        reminder_signout_hours: f.reminder_signout_hours ?? 10,
        reminder_abandon_warn_minutes: f.reminder_abandon_warn_minutes ?? 30,
        reminders_auto_enabled: f.reminders_auto_enabled ?? true,
        reminder_signin_enabled: f.reminder_signin_enabled ?? true,
        reminder_long_break_enabled: f.reminder_long_break_enabled ?? true,
        reminder_signout_enabled: f.reminder_signout_enabled ?? true,
        // Attendance keywords — fall back to defaults when the settings row hasn't got them yet.
        ...Object.fromEntries(
          KEYWORD_CATS.map(({ key }) => [
            key,
            Array.isArray(f[key]) && f[key].length ? f[key] : KEYWORD_DEFAULTS[key],
          ])
        ),
      };
      setForm(shape);
      setLoaded(shape);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Numeric fields use type="text" + inputMode to avoid native <input type="number">
  // quirks (some browsers/locales blank the field while typing); keep digits only.
  const setNum = (k) => (e) => set(k, e.target.value.replace(/\D/g, ""));
  const toggleDay = (d) => setForm((f) => ({ ...f, working_days: f.working_days.includes(d) ? f.working_days.filter((x) => x !== d) : [...f.working_days, d] }));
  const pickAccent = (c) => { set("brand_accent", c); setAccent(c); };

  // ── attendance keyword editing (one list per category) ──
  const setKeyword = (cat, idx, val) => setForm((f) => ({ ...f, [cat]: f[cat].map((k, i) => (i === idx ? val : k)) }));
  const addKeyword = (cat) => setForm((f) => ({ ...f, [cat]: [...f[cat], ""] }));
  const removeKeyword = (cat, idx) => setForm((f) => ({ ...f, [cat]: f[cat].filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    setSaving(true);
    const { status } = await updateSettings({
      ...form,
      day_start: form.day_start ? `${form.day_start}:00` : null,
      day_end: form.day_end ? `${form.day_end}:00` : null,
      grace_period_min: Number(form.grace_period_min) || 0,
      max_shift_hours: Number(form.max_shift_hours) || 18,
      reward_signin_deadline: form.reward_signin_deadline ? `${form.reward_signin_deadline}:00` : null,
      reward_min_hours: Number(form.reward_min_hours) || 0,
      reward_max_absent_days: Number(form.reward_max_absent_days) || 0,
      reminder_signin_after_minutes: Number(form.reminder_signin_after_minutes) || 0,
      reminder_long_break_min: Number(form.reminder_long_break_min) || 0,
      reminder_signout_hours: Number(form.reminder_signout_hours) || 0,
      reminder_abandon_warn_minutes: Number(form.reminder_abandon_warn_minutes) || 30,
      // Clean each keyword list, and never persist an empty list (fall back to defaults so the
      // bot always has something to detect).
      ...Object.fromEntries(
        KEYWORD_CATS.map(({ key }) => {
          const cleaned = cleanKeywordList(form[key]);
          return [key, cleaned.length ? cleaned : KEYWORD_DEFAULTS[key]];
        })
      ),
    });
    setSaving(false);
    if (status === 200) { toast.success("Settings saved"); setLoaded(form); refreshSettings(); }
    else toast.error("Couldn't save settings");
  };
  const handleCancel = () => { setForm(loaded); if (loaded?.brand_accent) setAccent(loaded.brand_accent); };

  // ── account handlers ──
  const noAcctChange = email === prevEmail && !password;
  const validateAcct = () => {
    const { error } = accountSchema.validate({ email, password, reTypedPassword }, { abortEarly: false });
    if (!error) { setErrors({}); return true; }
    setErrors(error.details.reduce((acc, d) => ({ ...acc, [d.context.key]: d.message }), {}));
    return false;
  };
  const handleAccount = async (e) => {
    e.preventDefault();
    if (!validateAcct()) return;
    setAcctSubmitting(true);
    const { status } = await updateAdmin(id, { email, password });
    setAcctSubmitting(false);
    if (status === 200) { toast.success("Account updated"); setUser({ ...getUser(), email }); setPassword(""); setReTypedPassword(""); }
    else toast.error("Something went wrong!");
  };

  if (loading || !form) {
    return (
      <div className="settings-page">
        {[0, 1, 2].map((i) => (
          <div className="set-card" key={i}><Skeleton width={160} height={18} /><div style={{ height: 14 }} /><Skeleton width="100%" height={42} /></div>
        ))}
      </div>
    );
  }

  return (
    <div className="settings-page">
      {/* Workspace */}
      <div className="set-card">
        <div className="set-title">Workspace</div>
        <div className="set-desc">Basic information about your organisation.</div>
        <div className="set-grid-2">
          <div className="set-field">
            <label>Organisation name</label>
            <input className="set-input" value={form.organisation_name} onChange={(e) => set("organisation_name", e.target.value)} />
          </div>
          <div className="set-field">
            <label>Time zone</label>
            <select className="set-select" value={form.timezone} onChange={(e) => set("timezone", e.target.value)}>
              {(TIMEZONES.includes(form.timezone) ? TIMEZONES : [form.timezone, ...TIMEZONES]).map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Working hours */}
      <div className="set-card">
        <div className="set-title">Working hours</div>
        <div className="set-desc">Used to flag late check-ins and compute hours.</div>
        <div className="set-grid-3">
          <div className="set-field"><label>Day starts</label><input type="time" className="set-input mono" value={form.day_start} onChange={(e) => set("day_start", e.target.value)} /></div>
          <div className="set-field"><label>Day ends</label><input type="time" className="set-input mono" value={form.day_end} onChange={(e) => set("day_end", e.target.value)} /></div>
          <div className="set-field"><label>Sign-in grace period (min)</label><input type="text" inputMode="numeric" className="set-input mono" value={form.grace_period_min} onChange={setNum("grace_period_min")} /></div>
        </div>
        <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-2)", marginBottom: 8 }}>Working days</label>
        <div className="workdays">
          {DAYS.map((d) => (
            <button type="button" key={d} className={`workday ${form.working_days.includes(d) ? "on" : ""}`} onClick={() => toggleDay(d)}>{d}</button>
          ))}
        </div>

        <div className="set-field" style={{ marginTop: 18, maxWidth: 240 }}>
          <label>Max shift hours</label>
          <input type="text" inputMode="numeric" className="set-input mono" value={form.max_shift_hours} onChange={setNum("max_shift_hours")} />
          <span className="set-desc" style={{ marginTop: 2 }}>Default 18. Measured from a member's sign-in. Keep it above the sign-out reminder window.</span>
        </div>
        <label className="set-check">
          <span>Auto-close stale sessions at this limit</span>
          <input type="checkbox" checked={!!form.sessions_auto_close_enabled} onChange={(e) => set("sessions_auto_close_enabled", e.target.checked)} />
        </label>
        <div className="set-desc">When on, a session left open past Max shift hours is closed automatically (sign-out capped at the limit and flagged as a missed sign-out), so forgotten sessions don't block the member's next sign-in.</div>

        {/* Send-reminder thresholds — global defaults; per-member overrides live on the profile. */}
        <div className="set-subhead">Reminders</div>
        <div className="set-desc">Reminders run automatically every few minutes and nudge each member at most once per day. Toggle each type on or off with the checkbox on its label; the value sets when it fires.</div>
        <label className="set-check">
          <span>Automatic reminders</span>
          <input type="checkbox" checked={!!form.reminders_auto_enabled} onChange={(e) => set("reminders_auto_enabled", e.target.checked)} />
        </label>
        <div className="set-grid-3">
          <div className="set-field">
            <label className="set-field-toggle">
              <input type="checkbox" checked={!!form.reminder_signin_enabled} onChange={(e) => set("reminder_signin_enabled", e.target.checked)} />
              <span>Sign-in (min after day start)</span>
            </label>
            <input type="text" inputMode="numeric" className="set-input mono" value={form.reminder_signin_after_minutes} onChange={setNum("reminder_signin_after_minutes")} disabled={!form.reminder_signin_enabled} />
          </div>
          <div className="set-field">
            <label className="set-field-toggle">
              <input type="checkbox" checked={!!form.reminder_long_break_enabled} onChange={(e) => set("reminder_long_break_enabled", e.target.checked)} />
              <span>Long break (min)</span>
            </label>
            <input type="text" inputMode="numeric" className="set-input mono" value={form.reminder_long_break_min} onChange={setNum("reminder_long_break_min")} disabled={!form.reminder_long_break_enabled} />
          </div>
          <div className="set-field">
            <label className="set-field-toggle">
              <input type="checkbox" checked={!!form.reminder_signout_enabled} onChange={(e) => set("reminder_signout_enabled", e.target.checked)} />
              <span>Sign-out after (hrs)</span>
            </label>
            <input type="text" inputMode="numeric" className="set-input mono" value={form.reminder_signout_hours} onChange={setNum("reminder_signout_hours")} disabled={!form.reminder_signout_enabled} />
          </div>
        </div>

        {/* Last-chance warning before the session crosses "Max shift hours" and is abandoned.
            Tied to that window rather than to the three types above, so it sits on its own. */}
        <div className="set-field" style={{ maxWidth: 240 }}>
          <label>Warn before auto-close (min)</label>
          <input type="text" inputMode="numeric" className="set-input mono" value={form.reminder_abandon_warn_minutes} onChange={setNum("reminder_abandon_warn_minutes")} />
          {/* <span className="set-desc" style={{ marginTop: 2, marginBottom: 0 }}>Default 30. Sent this many minutes before max shift hours, warning the member their session is about to close. Keep it above 5 — reminders only run every 5 minutes.</span> */}
        </div>
      </div>

      {/* Attendance keywords */}
      <div className="set-card">
        <div className="set-title">Attendance keywords</div>
        <div className="set-desc">Words the bot looks for in the #attendance channel to record each action. Edit or add keywords for any category; changes take effect within a minute.</div>
        {KEYWORD_CATS.map((cat) => (
          <div className="kw-group" key={cat.key}>
            <div className="kw-head">
              <label className="kw-label">{cat.label}</label>
              <span className="kw-hint">{cat.hint}</span>
            </div>
            <div className="kw-list">
              {(form[cat.key] || []).map((kw, idx) => (
                <div className="kw-row" key={idx}>
                  <input
                    className="set-input"
                    value={kw}
                    placeholder="keyword or phrase…"
                    onChange={(e) => setKeyword(cat.key, idx, e.target.value)}
                  />
                  <button type="button" className="kw-remove" onClick={() => removeKeyword(cat.key, idx)} title="Remove keyword" aria-label={`Remove keyword from ${cat.label}`}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
                  </button>
                </div>
              ))}
              <button type="button" className="kw-add" onClick={() => addKeyword(cat.key)} title={`Add ${cat.label} keyword`}>
                <span className="kw-add-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
                </span>
                <span>Add keyword</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Reward contest */}
      <div className="set-card">
        <div className="set-title">Reward contest</div>
        <div className="set-desc">Monthly reward eligibility criteria.</div>
        <div className="set-grid-3">
          <div className="set-field"><label>Sign-in deadline</label><input type="time" className="set-input mono" value={form.reward_signin_deadline} onChange={(e) => set("reward_signin_deadline", e.target.value)} /></div>
          <div className="set-field"><label>Min hours / day</label><input type="text" inputMode="numeric" className="set-input mono" value={form.reward_min_hours} onChange={setNum("reward_min_hours")} /></div>
          <div className="set-field"><label>Max absent days</label><input type="text" inputMode="numeric" className="set-input mono" value={form.reward_max_absent_days} onChange={setNum("reward_max_absent_days")} /></div>
        </div>
      </div>

      {/* Integrations */}
      <div className="set-card">
        <div className="set-title">Integrations</div>
        <div className="set-desc">Connect tools your team already uses.</div>
        <div className="integration">
          <div className="left">
            <div className="int-icon" style={{ background: "rgba(102,93,169,.20)" }}><SlackLogo /></div>
            <div><div className="int-name">Slack</div><div className="int-desc">Check in / out from #attendance</div></div>
          </div>
          {form.slack_connected
            ? <span className="int-badge"><span className="dot" />Connected</span>
            : <button type="button" className="int-connect" onClick={() => set("slack_connected", true)}>Connect</button>}
        </div>
        <div className="integration">
          <div className="left">
            <div className="int-icon" style={{ background: "rgba(59,123,216,.18)" }}><svg style={{ color: "#3B7BD8" }}><use href="#i-cal" /></svg></div>
            <div><div className="int-name">Google Calendar</div><div className="int-desc">Sync leave &amp; holidays</div></div>
          </div>
          {form.gcal_connected
            ? <span className="int-badge"><span className="dot" />Connected</span>
            : <button type="button" className="int-connect" onClick={() => set("gcal_connected", true)}>Connect</button>}
        </div>
        <AiConnectors />
      </div>

      {/* Brand accent */}
      <div className="set-card">
        <div className="set-title">Brand accent</div>
        <div className="set-desc">Pick the accent colour used across the console. Applies instantly.</div>
        <div className="swatches">
          {ACCENTS.map((c) => {
            const on = (form.brand_accent || "").toLowerCase() === c.toLowerCase();
            return (
              <div key={c} className="swatch" onClick={() => pickAccent(c)} title={c}
                style={on ? { background: c, border: `3px solid ${c}`, boxShadow: "0 0 0 2px var(--surface) inset" } : { background: c }} />
            );
          })}
        </div>
      </div>

      {/* Save / Cancel */}
      <div className="set-actions">
        <button type="button" className="btn btn-ghost" onClick={handleCancel}>Cancel</button>
        <button type="button" className="btn btn-brand" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
      </div>

      {/* Account */}
      <div className="set-card">
        <div className="set-title">Account</div>
        <div className="set-desc">{name}{title ? ` · ${title}` : ""} — update your admin sign-in.</div>
        <form onSubmit={handleAccount}>
          <div className="set-grid-2">
            <div className="set-field">
              <label>Email</label>
              <input className="set-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" />
              {errors.email && <span className="set-err">{errors.email}</span>}
            </div>
            <div className="set-field">
              <label>New password</label>
              <input className="set-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank to keep current" />
              {errors.password && <span className="set-err">{errors.password}</span>}
            </div>
          </div>
          {password && (
            <div className="set-field" style={{ marginTop: 16 }}>
              <label>Re-type new password</label>
              <input className="set-input" type="password" value={reTypedPassword} onChange={(e) => setReTypedPassword(e.target.value)} placeholder="Type your new password again" />
              {errors.reTypedPassword && <span className="set-err">{errors.reTypedPassword}</span>}
            </div>
          )}
          <div className="set-actions" style={{ marginTop: 16 }}>
            <button className="btn btn-brand" type="submit" disabled={acctSubmitting || noAcctChange}>{acctSubmitting ? "Updating…" : "Update account"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
